import { useEffect, useRef, useState } from 'react';
import { ChevronRight, MessageSquare } from 'lucide-react';
import { useSelection } from '../store/selection';
import { useDemoTour } from '../store/demoTour';
import { flyToPoint } from '../utils/camera-api';
import {
  useAssistantPanel,
  interpretarMensajeObsia,
  ESTADOS_ACTIVOS,
  TIMEOUT_ACTIVIDAD_MS,
} from '../store/assistantPanel';
import { toCanonicalId, canonicalToDisplayId } from '../utils/canonicalId';

/**
 * ObsiaChatWidget — asistente de consulta en lenguaje natural (OBSIA),
 * embebido por iframe como cajón plegable en el borde derecho. OBSIA es un
 * proyecto aparte: este componente no importa nada suyo, solo le manda el
 * regulador seleccionado por postMessage. Contrato en
 * obsia-santacoloma-liaison.md.
 */

// La URL lleva la clave de acceso de OBSIA, así que no puede salir en el
// bundle: `dist/` se sirve sin autenticación y un `curl` la extraería. La
// pide el navegador a nuestro gateway, que solo la entrega a admin
// (Pull-SC-OBSIA5). `VITE_OBSIA_URL` queda para desarrollo, donde no hay
// gateway ni clave.
const OBSIA_URL_DEV = import.meta.env.VITE_OBSIA_URL || '';

async function pedirUrl(): Promise<string> {
  if (OBSIA_URL_DEV) return OBSIA_URL_DEV;
  try {
    const r = await fetch('/api/obsia-url', { credentials: 'include' });
    if (!r.ok) return '';
    const { url } = await r.json();
    return typeof url === 'string' ? url : '';
  } catch {
    return '';
  }
}

export function ObsiaChatWidget() {
  const [loaded, setLoaded] = useState(false);
  const [url, setUrl] = useState<string | null>(null);
  // `open`/`mounted` viven en el store para que la tecla "a" (atajo global en
  // SantaColomaMapView) los alcance sin pasar por el árbol de componentes.
  const open = useAssistantPanel(s => s.open);
  const montado = useAssistantPanel(s => s.mounted);
  const activity = useAssistantPanel(s => s.activity);
  const activityDetail = useAssistantPanel(s => s.activityDetail);
  const trabajando = ESTADOS_ACTIVOS.includes(activity);
  const frame = useRef<HTMLIFrameElement>(null);
  const selectedId = useSelection(s => s.selectedId);

  useEffect(() => { pedirUrl().then(setUrl); }, []);

  const origen = url ? new URL(url, window.location.href).origin : '';

  useEffect(() => {
    if (!loaded || !origen) return;
    frame.current?.contentWindow?.postMessage(
      { type: 'obsia:select-node', id: selectedId == null ? null : toCanonicalId(selectedId) },
      origen,
    );
  }, [selectedId, loaded, origen]);

  // Con el foco dentro del iframe el keydown no cruza al documento padre
  // (otro origen), así que la tecla no puede cerrar el cajón desde dentro.
  // OBSIA reenvía el atajo por postMessage; mientras no lo haga, queda el
  // botón de plegar. Contrato en obsia-santacoloma-liaison.md.
  useEffect(() => {
    const onMessage = (e: MessageEvent) => {
      if (!origen || e.origin !== origen) return;
      const msg = interpretarMensajeObsia(e.data);
      if (!msg) return;
      switch (msg.tipo) {
        case 'toggle':
          useAssistantPanel.getState().toggle();
          break;
        case 'state':
          useAssistantPanel.getState().setActivity(msg.state, msg.detalle);
          break;
        case 'pan-to':
          // La cámara es compartida por WebSocket, igual que el auto-encuadre
          // de alarmas: si hay un operador con la cámara en manual, su
          // CameraController ignora el vuelo y no se le mueve el mapa.
          flyToPoint(msg.center, msg.zoom ?? undefined);
          break;
        case 'highlight':
          // Mismo array que pinta el tour. Lanzar el tour lo vacía, y así
          // debe ser: el tour manda sobre lo que el asistente señaló.
          useDemoTour.setState({
            highlights: msg.assets
              .map(canonicalToDisplayId)
              .filter((id): id is string => id !== null),
          });
          break;
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [origen]);

  // Si el widget muere a mitad de una consulta (crash, red, backend caído)
  // deja de emitir y el estado se congela: el cajón diría "sintetizando"
  // para siempre y cualquier comprobación automática se colgaría esperando.
  // El host no puede arreglar la consulta, pero sí dejar de mentir sobre ella.
  useEffect(() => {
    if (!ESTADOS_ACTIVOS.includes(activity)) return;
    const id = setTimeout(
      () => useAssistantPanel.getState().setActivity('error'),
      TIMEOUT_ACTIVIDAD_MS,
    );
    return () => clearTimeout(id);
  }, [activity, activityDetail]);

  if (!url) return null;

  return (
    <>
      {/* Pestaña vertical en el borde: visible solo con el cajón plegado */}
      {!open && (
        <button
          onClick={() => useAssistantPanel.getState().toggle()}
          className="fixed right-0 top-1/2 -translate-y-1/2 z-50 flex items-center gap-1.5 px-2 py-3 rounded-l-lg border border-r-0
                     border-gray-700 bg-gray-900/90 backdrop-blur-sm text-gray-300 hover:bg-gray-800 shadow-lg transition-colors"
          title={trabajando
            ? 'El asistente está preparando una respuesta (A o Q)'
            : 'Preguntar sobre los reguladores (A o Q)'}
          style={{ writingMode: 'vertical-rl' }}
        >
          <MessageSquare className="w-3.5 h-3.5 rotate-90" />
          <span className="text-[11px] font-medium tracking-wide">Preguntar</span>
          {/* Plegado, el iframe no se ve: sin esto, quien pregunta y pliega
              no sabe cuándo volver. Acordado con OBSIA que lo pinte el host
              (Pull-SC-OBSIA6). */}
          {trabajando && (
            <span
              className="w-1.5 h-1.5 rounded-full bg-cyan-400 motion-safe:animate-pulse"
              aria-hidden="true"
            />
          )}
        </button>
      )}

      {/* El cajón se oculta en vez de desmontarse: la conversación
          sobrevive a plegarlo y desplegarlo. */}
      <div
        className={`fixed top-[60px] bottom-3 right-0 z-50 w-[750px] max-w-[90vw] flex flex-col
                    rounded-l-lg border border-r-0 border-gray-700 bg-gray-900/95 backdrop-blur-sm shadow-2xl overflow-hidden`}
        style={{ transform: open ? 'translateX(0)' : 'translateX(100%)' }}
        aria-hidden={!open}
        data-assistant-state={activity}
        data-assistant-detail={activityDetail ?? undefined}
      >
        <div className="flex items-center gap-2 px-3 py-1.5 border-b border-gray-800 bg-gray-900">
          <button
            onClick={() => useAssistantPanel.getState().close()}
            className="p-1 -ml-1 text-gray-500 hover:text-gray-300 transition-colors"
            title="Plegar asistente (A o Q)"
            aria-label="Plegar asistente"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
          <MessageSquare className="w-3 h-3 text-cyan-400" />
          <span className="text-[11px] font-medium text-gray-200">Asistente</span>
        </div>
        {montado && (
          <iframe
            ref={frame}
            src={url}
            title="Asistente OBSIA"
            onLoad={() => setLoaded(true)}
            sandbox="allow-scripts allow-same-origin allow-forms"
            className="flex-1 w-full border-0 bg-gray-950"
          />
        )}
      </div>
    </>
  );
}
